import React, { useEffect, useMemo, useState } from "react";
import { BookPlus, FileSpreadsheet, PackagePlus, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ConferenceStat, ConferenceStatus, EmptyConferenceState, ExportButtons } from "./ConferenceShared";

const EMPTY_CONFIG = {
  category_key: "fixed_500",
  total_books: 600,
  leaves_per_book: 10,
  starting_book_number: 1,
  book_direction: "ascending",
  starting_receipt_number: 1,
  receipt_direction: "ascending",
  receipt_increment: 1,
  book_prefix: "",
  book_suffix: "",
  receipt_prefix: "",
  receipt_suffix: "",
};

export default function ConferenceStockPanel({ meta, canManage, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [books, setBooks] = useState([]);
  const [filters, setFilters] = useState({ category: "all", status: "all", search: "" });
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [batchMeta, setBatchMeta] = useState({ batch_name: "", printing_date: "", received_date: "", supplier: "", remarks: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editBook, setEditBook] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [manualBook, setManualBook] = useState({ batch_id: "", book_number: "", start_receipt_number: "", end_receipt_number: "", leaves: "", reason: "" });

  const categories = meta?.categories || [];

  const load = async () => {
    try {
      const [s, b, booksRes] = await Promise.all([
        api.get("/conference/stock/dashboard"),
        api.get("/conference/stock/batches"),
        api.get("/conference/stock/books", { params: { limit: 1000 } }),
      ]);
      setSummary(s.data); setBatches(b.data); setBooks(booksRes.data);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load receipt stock"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filteredBooks = useMemo(() => books.filter(b => {
    if (filters.category !== "all" && b.category_key !== filters.category) return false;
    if (filters.status !== "all" && b.status !== filters.status) return false;
    const q = filters.search.trim().toLowerCase();
    if (!q) return true;
    return [b.book_number, b.batch_name, b.current_district, b.assigned_member_name, b.assigned_membership_id]
      .some(v => (v || "").toLowerCase().includes(q));
  }), [books, filters]);

  const stockRows = [["Category", "Batch", "Book", "Receipt Start", "Receipt End", "Leaves", "District", "Member", "Status"], ...filteredBooks.map(b => [b.category_label, b.batch_name, b.book_number, b.receipt_start, b.receipt_end, b.leaves_count, b.current_district || "", b.assigned_member_name || "", b.status])];

  const setCategory = (key) => {
    const cat = categories.find(c => c.key === key);
    setConfig(c => ({
      ...c,
      category_key: key,
      total_books: cat?.default_books || c.total_books,
      leaves_per_book: cat?.default_leaves || c.leaves_per_book,
    }));
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/conference/stock/preview", config);
      setPreview(data);
      toast.success(`Preview ready: ${data.total_books} books / ${data.total_leaves} leaves`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const saveBatch = async () => {
    if (!batchMeta.batch_name.trim()) { toast.error("Batch name is required"); return; }
    setBusy(true);
    try {
      await api.post("/conference/stock/batches", { ...config, ...batchMeta, books: preview?.books || undefined });
      toast.success("Receipt stock batch saved");
      setPreview(null); setBatchMeta({ batch_name: "", printing_date: "", received_date: "", supplier: "", remarks: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save stock"); }
    finally { setBusy(false); }
  };

  const importExcel = async (file) => {
    if (!file || !manualBook.batch_id) { toast.error("Choose a batch first"); return; }
    const fd = new FormData(); fd.append("batch_id", manualBook.batch_id); fd.append("file", file);
    try {
      toast.loading("Importing books…", { id: "stock-import" });
      await api.post("/conference/stock/import", fd);
      toast.success("Books imported", { id: "stock-import" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed", { id: "stock-import" }); }
  };

  const submitManualBook = async () => {
    try {
      await api.post("/conference/stock/books", {
        batch_id: manualBook.batch_id,
        reason: manualBook.reason,
        book: {
          book_number: manualBook.book_number,
          book_number_numeric: Number((manualBook.book_number.match(/\d+/) || [0])[0]),
          receipt_start: String(manualBook.start_receipt_number),
          receipt_end: String(manualBook.end_receipt_number),
          start_receipt_numeric: Number(manualBook.start_receipt_number),
          end_receipt_numeric: Number(manualBook.end_receipt_number),
          leaves_count: Number(manualBook.leaves),
        },
      });
      toast.success("Book added"); setAddOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add book"); }
  };

  const saveBookEdit = async () => {
    try {
      await api.patch(`/conference/stock/books/${editBook.id}`, {
        book_number: editForm.book_number,
        book_number_numeric: Number((editForm.book_number.match(/\d+/) || [0])[0]),
        receipt_start: editForm.receipt_start,
        receipt_end: editForm.receipt_end,
        start_receipt_numeric: Number(editForm.start_receipt_numeric),
        end_receipt_numeric: Number(editForm.end_receipt_numeric),
        leaves_count: Number(editForm.leaves_count),
        reason: editForm.reason,
      });
      toast.success("Book numbering corrected"); setEditBook(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Correction failed"); }
  };

  const statusCards = summary?.status_counts || {};
  return (
    <div className="space-y-5" data-testid="conference-stock-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <ConferenceStat testId="stock-total" label="Printed Books / മൊത്തം ബുക്കുകൾ" value={summary?.total_books ?? 0} />
        <ConferenceStat testId="stock-available" label="Available" value={statusCards.available || 0} tone="green" />
        <ConferenceStat testId="stock-allocated" label="Allocated" value={(statusCards.allocated_to_district || 0) + (statusCards.received_by_district || 0)} tone="orange" />
        <ConferenceStat testId="stock-assigned" label="Assigned" value={(statusCards.assigned_to_member || 0) + (statusCards.partially_used || 0) + (statusCards.fully_used || 0)} tone="blue" />
        <ConferenceStat testId="stock-returned" label="Returned / Damaged" value={(statusCards.returned || 0) + (statusCards.damaged || 0)} tone="red" />
      </div>

      {canManage && (
        <Card className="p-5" data-testid="stock-numbering-studio">
          <div className="flex items-center gap-2 mb-4">
            <PackagePlus className="w-5 h-5 text-brand-blue" />
            <div>
              <h3 className="font-black">Receipt Stock & Numbering Studio</h3>
              <p className="text-xs text-muted-foreground">Book and receipt directions are independent. Preview before saving.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-3">
            <Field label="Receipt Category"><Select value={config.category_key} onValueChange={setCategory}><SelectTrigger data-testid="stock-category"><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></Field>
            <NumField label="Total Books" v={config.total_books} set={v => setConfig(c => ({ ...c, total_books: Number(v) }))} testId="stock-total-books" />
            <NumField label="Leaves / Book" v={config.leaves_per_book} set={v => setConfig(c => ({ ...c, leaves_per_book: Number(v) }))} testId="stock-leaves" />
            <NumField label="Start Book No." v={config.starting_book_number} set={v => setConfig(c => ({ ...c, starting_book_number: Number(v) }))} testId="stock-start-book" />
            <Field label="Book Direction"><Select value={config.book_direction} onValueChange={v => setConfig(c => ({ ...c, book_direction: v }))}><SelectTrigger data-testid="stock-book-direction"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ascending">Ascending</SelectItem><SelectItem value="descending">Descending</SelectItem></SelectContent></Select></Field>
            <NumField label="Start Receipt No." v={config.starting_receipt_number} set={v => setConfig(c => ({ ...c, starting_receipt_number: Number(v) }))} testId="stock-start-receipt" />
            <Field label="Receipt Direction"><Select value={config.receipt_direction} onValueChange={v => setConfig(c => ({ ...c, receipt_direction: v }))}><SelectTrigger data-testid="stock-receipt-direction"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ascending">Ascending</SelectItem><SelectItem value="descending">Descending</SelectItem></SelectContent></Select></Field>
            <NumField label="Receipt Increment" v={config.receipt_increment} set={v => setConfig(c => ({ ...c, receipt_increment: Number(v) }))} testId="stock-receipt-increment" />
            <Field label="Book Prefix"><Input value={config.book_prefix} onChange={e => setConfig(c => ({ ...c, book_prefix: e.target.value }))} data-testid="stock-book-prefix" /></Field>
            <Field label="Receipt Prefix"><Input value={config.receipt_prefix} onChange={e => setConfig(c => ({ ...c, receipt_prefix: e.target.value }))} data-testid="stock-receipt-prefix" /></Field>
          </div>
          <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-3 mt-3">
            <Field label="Batch Name / Number"><Input value={batchMeta.batch_name} onChange={e => setBatchMeta(m => ({ ...m, batch_name: e.target.value }))} placeholder="CONF-2026-500-A" data-testid="stock-batch-name" /></Field>
            <Field label="Printing Date"><Input type="date" value={batchMeta.printing_date} onChange={e => setBatchMeta(m => ({ ...m, printing_date: e.target.value }))} data-testid="stock-printing-date" /></Field>
            <Field label="Date Received"><Input type="date" value={batchMeta.received_date} onChange={e => setBatchMeta(m => ({ ...m, received_date: e.target.value }))} data-testid="stock-received-date" /></Field>
            <Field label="Supplier / Press"><Input value={batchMeta.supplier} onChange={e => setBatchMeta(m => ({ ...m, supplier: e.target.value }))} data-testid="stock-supplier" /></Field>
            <Field label="Remarks"><Input value={batchMeta.remarks} onChange={e => setBatchMeta(m => ({ ...m, remarks: e.target.value }))} data-testid="stock-remarks" /></Field>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="stock-preview-btn">Preview Generated Books</Button>
            <Button onClick={saveBatch} disabled={busy} className="bg-brand-blue text-white" data-testid="stock-save-btn">Save Stock Batch</Button>
            <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="stock-add-book-btn"><BookPlus className="w-4 h-4 mr-1.5" /> Add Book</Button>
            <label className="inline-flex">
              <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={e => importExcel(e.target.files?.[0])} data-testid="stock-import-input" />
              <Button variant="outline" asChild><span><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Import Excel</span></Button>
            </label>
            <Select value={manualBook.batch_id} onValueChange={v => setManualBook(m => ({ ...m, batch_id: v }))}>
              <SelectTrigger className="w-56" data-testid="stock-import-batch"><SelectValue placeholder="Batch for import" /></SelectTrigger>
              <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.batch_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {preview && (
            <div className="mt-4 rounded-lg border border-border overflow-hidden" data-testid="stock-preview-table">
              <div className="p-3 bg-muted/40 text-sm font-semibold">Preview: {preview.total_books} books · {preview.total_leaves} leaves</div>
              <div className="max-h-56 overflow-auto"><Table><TableHeader><TableRow><TableHead>Book</TableHead><TableHead>Receipt Start</TableHead><TableHead>Receipt End</TableHead><TableHead>Leaves</TableHead></TableRow></TableHeader><TableBody>{preview.books.slice(0, 100).map(b => <TableRow key={b.book_number}><TableCell>{b.book_number}</TableCell><TableCell>{b.receipt_start}</TableCell><TableCell>{b.receipt_end}</TableCell><TableCell>{b.leaves_count}</TableCell></TableRow>)}</TableBody></Table></div>
              {preview.books.length > 100 && <div className="p-2 text-xs text-muted-foreground">Showing first 100 books. Full set will be saved.</div>}
            </div>
          )}
        </Card>
      )}

      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="font-black">Receipt Book Stock Register / രസീത് സ്റ്റോക്ക്</h3>
            <p className="text-xs text-muted-foreground">{filteredBooks.length} books shown</p>
          </div>
          <ExportButtons rows={stockRows} title="Receipt Stock Register" filename="kelta_conference_stock_register" testId="stock-export" />
        </div>
        <div className="grid md:grid-cols-4 gap-2 mb-3">
          <Select value={filters.category} onValueChange={v => setFilters(f => ({ ...f, category: v }))}><SelectTrigger data-testid="stock-filter-category"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v }))}><SelectTrigger data-testid="stock-filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All statuses</SelectItem>{meta?.book_statuses?.map(s => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent></Select>
          <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="Search book/member/district…" data-testid="stock-search" />
          <Button variant="outline" onClick={load} data-testid="stock-refresh-btn"><RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh</Button>
        </div>
        {filteredBooks.length === 0 ? <EmptyConferenceState title="No receipt books yet" text="Register a stock batch using the numbering studio above." /> : (
          <div className="overflow-auto max-h-[560px] rounded-md border border-border" data-testid="stock-books-table">
            <Table>
              <TableHeader className="sticky top-0 bg-card"><TableRow><TableHead>Book</TableHead><TableHead>Category</TableHead><TableHead>Receipt Range</TableHead><TableHead>Leaves</TableHead><TableHead>Batch</TableHead><TableHead>District</TableHead><TableHead>Member</TableHead><TableHead>Status</TableHead>{canManage && <TableHead className="text-right">Action</TableHead>}</TableRow></TableHeader>
              <TableBody>{filteredBooks.map(b => (
                <TableRow key={b.id} data-testid={`stock-book-${b.id}`}>
                  <TableCell className="font-mono font-bold">{b.book_number}</TableCell>
                  <TableCell>{b.category_label}</TableCell>
                  <TableCell className="font-mono">{b.receipt_start}–{b.receipt_end}</TableCell>
                  <TableCell>{b.leaves_count}</TableCell>
                  <TableCell>{b.batch_name}</TableCell>
                  <TableCell>{b.current_district || "State Stock"}</TableCell>
                  <TableCell>{b.assigned_member_name || "—"}</TableCell>
                  <TableCell><ConferenceStatus value={b.status} /></TableCell>
                  {canManage && <TableCell className="text-right"><Button size="sm" variant="outline" disabled={b.status !== "available"} onClick={() => { setEditBook(b); setEditForm({ ...b, reason: "" }); }} data-testid={`stock-edit-${b.id}`}>Correct</Button></TableCell>}
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add Individual Receipt Book</DialogTitle><DialogDescription>Add one book to an existing batch. Duplicate and overlapping ranges are rejected.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Batch"><Select value={manualBook.batch_id} onValueChange={v => setManualBook(m => ({ ...m, batch_id: v }))}><SelectTrigger data-testid="manual-book-batch"><SelectValue placeholder="Choose batch" /></SelectTrigger><SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.batch_name}</SelectItem>)}</SelectContent></Select></Field>
            <div className="grid grid-cols-2 gap-2"><Field label="Book No"><Input value={manualBook.book_number} onChange={e => setManualBook(m => ({ ...m, book_number: e.target.value }))} data-testid="manual-book-number" /></Field><Field label="Leaves"><Input type="number" value={manualBook.leaves} onChange={e => setManualBook(m => ({ ...m, leaves: e.target.value }))} data-testid="manual-book-leaves" /></Field><Field label="Receipt Start"><Input type="number" value={manualBook.start_receipt_number} onChange={e => setManualBook(m => ({ ...m, start_receipt_number: e.target.value }))} data-testid="manual-book-start" /></Field><Field label="Receipt End"><Input type="number" value={manualBook.end_receipt_number} onChange={e => setManualBook(m => ({ ...m, end_receipt_number: e.target.value }))} data-testid="manual-book-end" /></Field></div>
            <Field label="Reason"><Textarea value={manualBook.reason} onChange={e => setManualBook(m => ({ ...m, reason: e.target.value }))} data-testid="manual-book-reason" /></Field>
            <Button onClick={submitManualBook} className="w-full bg-brand-blue text-white" data-testid="manual-book-save">Add Book</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editBook} onOpenChange={o => !o && setEditBook(null)}>
        <DialogContent><DialogHeader><DialogTitle>Correct Book Numbering</DialogTitle><DialogDescription>Only unused and unassigned books can be renumbered. A reason is required for audit.</DialogDescription></DialogHeader>
          {editBook && <div className="space-y-3"><div className="grid grid-cols-2 gap-2"><Field label="Book No"><Input value={editForm.book_number || ""} onChange={e => setEditForm(f => ({ ...f, book_number: e.target.value }))} data-testid="edit-book-number" /></Field><Field label="Leaves"><Input type="number" value={editForm.leaves_count || ""} onChange={e => setEditForm(f => ({ ...f, leaves_count: e.target.value }))} data-testid="edit-book-leaves" /></Field><Field label="Receipt Start"><Input type="number" value={editForm.start_receipt_numeric || ""} onChange={e => setEditForm(f => ({ ...f, start_receipt_numeric: e.target.value, receipt_start: e.target.value }))} data-testid="edit-book-start" /></Field><Field label="Receipt End"><Input type="number" value={editForm.end_receipt_numeric || ""} onChange={e => setEditForm(f => ({ ...f, end_receipt_numeric: e.target.value, receipt_end: e.target.value }))} data-testid="edit-book-end" /></Field></div><Field label="Audit Reason"><Textarea value={editForm.reason || ""} onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))} data-testid="edit-book-reason" /></Field><Button onClick={saveBookEdit} className="w-full bg-brand-blue text-white" data-testid="edit-book-save">Save Correction</Button></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>; }
function NumField({ label, v, set, testId }) { return <Field label={label}><Input type="number" value={v} onChange={e => set(e.target.value)} data-testid={testId} /></Field>; }
